import BrowserUpdatedOutlinedIcon from '@mui/icons-material/BrowserUpdatedOutlined';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';

import { useOperationalTab } from '../contexts/OperationalTabContext';

/**
 * Prevent more than one authenticated application tab from operating at the same time.
 *
 * The backend already enforces single session across devices/logins. This gate
 * complements that rule inside the same browser profile: only one tab may keep
 * the authenticated application active for operational telephony/runtime usage.
 */
export default function OperationalTabGate({ children }: { children: ReactNode }) {
  const { t } = useTranslation('common');
  const {
    isReady,
    isEnforced,
    shouldBlockApp,
    decisionRequired,
    blockingReason,
    takeOverHere,
    keepUsingOtherTab,
  } = useOperationalTab();

  if (!isEnforced) {
    return <>{children}</>;
  }

  if (!isReady) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 3,
          bgcolor: 'background.default',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('singleOperationalTab.checking')}
        </Typography>
      </Box>
    );
  }

  if (!shouldBlockApp) {
    return <>{children}</>;
  }

  const title = decisionRequired
    ? t('singleOperationalTab.dialog.title')
    : blockingReason === 'taken_over'
      ? t('singleOperationalTab.dialog.takenOverTitle')
      : t('singleOperationalTab.dialog.blockedTitle');

  const body = decisionRequired
    ? t('singleOperationalTab.dialog.description')
    : blockingReason === 'taken_over'
      ? t('singleOperationalTab.dialog.takenOverBody')
      : t('singleOperationalTab.dialog.blockedBody');

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
      }}
    >
      <Dialog
        open
        fullWidth
        maxWidth="sm"
        disableEscapeKeyDown
        onClose={(_, reason) => {
          if (reason === 'backdropClick') {
            return;
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <BrowserUpdatedOutlinedIcon color="primary" />
            <Typography variant="h6">{title}</Typography>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Stack spacing={2}>
            <Typography color="text.secondary">
              {body}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('singleOperationalTab.dialog.helper')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          {decisionRequired ? (
            <Button onClick={keepUsingOtherTab} color="inherit">
              {t('singleOperationalTab.actions.useOtherTab')}
            </Button>
          ) : null}
          <Button variant="contained" onClick={takeOverHere}>
            {t('singleOperationalTab.actions.useHere')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
